import styled, { css, keyframes } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

// types
import type { StyledContentBoxProps } from './ContentBox.types';

const StyledDefaultContentBox = styled.div<StyledContentBoxProps>((props) => {
  const { animation, color, position, theme } = props;

  return css`
    ${getDefaultStyles({ color, theme })};

    ${getPosition({ position })};

    ${getBackdropStyles({ color })};

    ${() => (animation ? getAnimation({ animation, position }) : undefined)};

    h3 {
      margin: 0 !important;
    }

    .content-box__heading,
    .content-box__subheading {
      ${() => getBasicFontStyles({ color, theme })}
    }

    .content-box__subheading {
      display: block;
    }

    .content-box__button {
      align-self: flex-end;

      margin-top: 1.5rem;
      @media ${({ theme }) => theme.device.medium} {
        margin-top: 3rem;
      }
    }
  `;
});

const StyledContentBoxDefault = styled(StyledDefaultContentBox)<StyledContentBoxProps>(() => {
  return css`
    text-transform: uppercase;

    h1 {
      display: flex;
      flex-direction: column-reverse;
    }
  `;
});

const StyledContentBoxNord = styled(StyledDefaultContentBox)<StyledContentBoxProps>(() => {
  return css`
    &&& .content-box__subheading {
      font-family: 'QuickbrushBaltics', helvetica, arial, sans-serif;
    }
  `;
});

const StyledContentBoxKolo = styled(StyledDefaultContentBox)<StyledContentBoxProps>((props) => {
  const { theme } = props;
  return css`
    &&& .content-box__heading,
    &&& .content-box__subheading {
      text-transform: uppercase;
      font-weight: ${theme.fontWeights.bold};
    }
  `;
});

const StyledContentBoxTwyford = styled(StyledDefaultContentBox)<StyledContentBoxProps>((props) => {
  const { theme } = props;
  return css`
    &&& .content-box__subheading {
      font-weight: ${theme.fontWeights.regular};
      text-transform: capitalize;
    }
  `;
});

const StyledContentBoxGdds = styled(StyledDefaultContentBox)<StyledContentBoxProps>((props) => {
  const { theme } = props;
  return css`
    &&& .content-box__heading,
    &&& .content-box__subheading {
      text-transform: uppercase;
    }

    &&& .content-box__heading--emphasized,
    &&& .content-box__subheading--emphasized {
      font-weight: ${theme.fontWeights.bold};
    }
  `;
});

export const StyledContentBox = {
  Default: StyledContentBoxDefault,
  Gdds: StyledContentBoxGdds,
  Nord: StyledContentBoxNord,
  Kolo: StyledContentBoxKolo,
  Twyford: StyledContentBoxTwyford,
};

function getBackdropStyles({ color }: Pick<StyledContentBoxProps, 'color'>) {
  const colorBackground = color === 'black' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
  return css`
    background: ${colorBackground};
  `;
}

function getDefaultStyles({
  color,
  theme,
}: Pick<StyledContentBoxProps, 'color'> & { theme: DefaultTheme }) {
  return css`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 1.5rem 2rem;
    z-index: ${theme.zIndices['20']};

    h1 {
      box-sizing: border-box;
      width: ${theme.sizes.full};
      max-width: ${theme.sizes.full};
      min-width: min-content;
      color: ${color === 'white' ? theme.colors.white : theme.colors.black};
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
    }
    h1 span {
      white-space: pre-line !important;
    }
  `;
}

function getPosition({ position }: Pick<StyledContentBoxProps, 'position'>) {
  return css`
    top: ${() => (position === 'top' ? '35%' : '65%')};
    transform: ${() => (position === 'top' ? 'translateY(-35%)' : 'translateY(-65%)')};
  `;
}

function getAnimation({
  animation,
  position,
}: Pick<StyledContentBoxProps, 'animation' | 'position'>) {
  if (!animation)
    css`
      animation: '';
    `;

  return css`
    animation: ${animateFadeIn({ position })} 1.5s ease-in both;
  `;
}

const slideUpKeyFrames = keyframes`
  from {
    top: 100%;
  }

  to {
    top: 35%;
  }
`;

const slideDownKeyFrames = keyframes`
  from {
    top: 0%;
  }

  to {
    top: 65%;
  }
`;

function animateFadeIn({ position }: Pick<StyledContentBoxProps, 'position'>) {
  return css`
    ${position === 'bottom' ? slideDownKeyFrames : slideUpKeyFrames}
  `;
}

function getBasicFontStyles({
  color,
  theme,
}: Pick<StyledContentBoxProps, 'color'> & { theme: DefaultTheme }) {
  return css`
    width: ${theme.sizes.full};
    margin-bottom: ${theme.sizes[0]};
    font-weight: ${theme.fontWeights.light};
    letter-spacing: ${theme.letterSpacings.normal};

    color: ${color === 'black' ? theme.colors.black : theme.colors.white};

    white-space: break-spaces;
    word-wrap: break-word;
    word-break: break-word;

    b {
      display: inline-block;
    }
  `;
}
